import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Filter,
  Sparkles,
  Rocket,
  Zap,
  X,
  AlertCircle,
  Bot,
  SearchX,
} from "lucide-react";
import AgentCard from "@/components/AgentCard";
import SearchBar from "@/components/SearchBar";
import type { SearchFilters } from "@/types";
import { useAgents, useTags } from "@/hooks/useAgents";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { debounce } from "@/lib/data-transform";

const Home: React.FC = () => {
  const [filters, setFilters] = useState<SearchFilters>({
    query: "",
    tags: [],
    sortBy: "newest",
  });

  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounce search query
  const debouncedSetQuery = useMemo(
    () => debounce((query: string) => setDebouncedQuery(query), 300),
    [],
  );

  useEffect(() => {
    debouncedSetQuery(filters.query);
  }, [filters.query, debouncedSetQuery]);

  // Load agents with current filters
  const {
    agents,
    loading,
    error,
    hasMore,
    total,
    loadMore,
    refresh,
    loadingMore,
  } = useAgents({
    search: debouncedQuery,
    tags: filters.tags,
    sortBy: filters.sortBy,
    autoLoad: true,
  });

  // Load available tags
  const { tags: availableTags, loading: tagsLoading } = useTags();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleTagToggle = (tag: string) => {
    setFilters((prev) => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter((t) => t !== tag)
        : [...prev.tags, tag],
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      query: "",
      tags: [],
      sortBy: "newest",
    });
    setShowFilterDropdown(false);
  };

  // Handle load more agents
  const handleLoadMore = async () => {
    await loadMore();
  };

  // Infinite scroll setup
  const { targetRef } = useIntersectionObserver<HTMLDivElement>({
    onIntersect: () => {
      if (!loadingMore && hasMore && agents.length > 0) {
        handleLoadMore();
      }
    },
    rootMargin: "100px", // Trigger 100px before the element comes into view
    enabled: !loading && !error && hasMore,
  });

  // Handle refresh
  const handleRefresh = async () => {
    await refresh();
  };
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-slate-50 to-blue-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="container mx-auto px-4 py-20 text-center relative">
          <div className="mx-auto max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 text-slate-900 dark:text-white">
              Discover{" "}
              <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                Infrastructure Agents
              </span>
            </h1>
            <p className="text-xl text-slate-600 dark:text-slate-300 mb-8 max-w-2xl mx-auto">
              From Infrastructure as Code to Intelligent Infrastructure —
              explore, share, and extend community-built agents.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <div className="w-full max-w-2xl">
                <SearchBar filters={filters} onFiltersChange={setFilters} />
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-sm text-slate-600 dark:text-slate-300">
              <div className="flex items-center gap-2">
                <Rocket className="h-4 w-4 text-orange-500" />
                <span>Agentic Infrastructure</span>
              </div>
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-blue-500" />
                <span>Autonomous Systems</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-yellow-500" />
                <span>Community Driven</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-500" />
                <span>Infrastructure as Agent</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agents Grid */}
      <section className="container mx-auto px-4 py-16">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold mb-2">Explore Agents</h2>
            <p className="text-muted-foreground">
              Discover what the community is building and share your own
            </p>
          </div>

          <div className="flex items-center justify-center md:justify-end gap-4 mt-4 md:mt-0 w-full md:w-auto">
            <div
              className="relative flex justify-center md:justify-start"
              ref={dropdownRef}
            >
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="inline-flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2.5 text-sm font-medium transition-all duration-200 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-slate-200 dark:focus:ring-slate-600 focus:border-slate-300 dark:focus:border-slate-600"
              >
                <Filter className="mr-2 h-4 w-4" />
                Filter {filters.tags.length > 0 && `(${filters.tags.length})`}
              </button>

              {showFilterDropdown && (
                <div className="absolute left-1/2 transform -translate-x-1/2 mt-12 md:left-auto md:right-0 md:transform-none md:top-0 md:mt-0 w-80 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl shadow-slate-200/20 dark:shadow-slate-900/20 backdrop-blur-sm z-50 ring-1 ring-slate-200/50 dark:ring-slate-700/50">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                        Filter by Tags
                      </h3>
                      <button
                        onClick={() => setShowFilterDropdown(false)}
                        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {filters.tags.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Selected Tags ({filters.tags.length})
                          </span>
                          <button
                            onClick={clearAllFilters}
                            className="text-xs text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300"
                          >
                            Clear All
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-slate-200 dark:border-slate-600">
                          {filters.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center rounded-full bg-orange-100 dark:bg-orange-900/30 px-3 py-1.5 text-sm font-medium text-orange-800 dark:text-orange-300 shadow-sm ring-1 ring-orange-200/50 dark:ring-orange-800/50"
                            >
                              {tag}
                              <button
                                onClick={() => handleTagToggle(tag)}
                                className="ml-2 hover:text-orange-600 dark:hover:text-orange-200"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Available Tags
                      </span>
                      <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                        {tagsLoading ? (
                          <div className="col-span-2 text-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto"></div>
                          </div>
                        ) : (
                          availableTags
                            .filter((tag) => !filters.tags.includes(tag.name))
                            .map((tag) => (
                              <button
                                key={tag.id}
                                onClick={() => handleTagToggle(tag.name)}
                                className="text-left px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-500 transition-all duration-150 hover:shadow-sm"
                              >
                                {tag.name}
                              </button>
                            ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && agents.length === 0 && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <div className="text-red-600 dark:text-red-400 mb-4">
              <AlertCircle className="h-8 w-8 mx-auto mb-2" />
              <p className="text-lg font-medium">Failed to load agents</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
            <button
              onClick={handleRefresh}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Agents Grid */}
        {!loading || agents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {agents.map((agent) => (
              <AgentCard key={agent.id} agent={agent} />
            ))}
          </div>
        ) : null}

        {/* Empty State */}
        {!loading && !error && agents.length === 0 && (
          <div className="text-center py-16">
            <div className="max-w-2xl mx-auto">
              <div className="legal-page-notice-box p-6 rounded-lg">
                <div className="flex flex-col items-center space-y-4">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                    <SearchX
                      className="h-10 w-10 text-blue-600 dark:text-blue-400"
                      strokeWidth={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold legal-page-notice-text">
                      No agents found matching your criteria
                    </h3>
                    <p className="legal-page-notice-text text-sm max-w-md">
                      Try adjusting your search terms or filters to find what
                      you're looking for.
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setFilters({ query: "", tags: [], sortBy: "newest" })
                    }
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors font-medium"
                  >
                    <X className="h-4 w-4" />
                    Clear filters and show all agents
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Load More Button */}
        {!loading && !error && agents.length > 0 && hasMore && (
          <div ref={targetRef} className="text-center mt-12">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Loading...
                </>
              ) : (
                "Load More Agents"
              )}
            </button>
          </div>
        )}

        {/* Results Summary */}
        {!loading && !error && agents.length > 0 && (
          <div className="text-center mt-8 text-sm text-muted-foreground">
            Showing {agents.length} of {total} agents
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="bg-muted/30">
        <div className="container mx-auto px-4 py-16 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to share Infrastructure Agent?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join endless of developers sharing infrastructure automation
            solutions. Make deployment easier for everyone.
          </p>
          <a
            href="/submit"
            className="inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-orange-500 to-red-500 px-8 py-4 text-base font-semibold text-white shadow-lg transition-all duration-200 hover:from-orange-600 hover:to-red-600 hover:shadow-xl hover:scale-105 focus:outline-none focus:ring-4 focus:ring-orange-200 dark:focus:ring-orange-800"
          >
            <Rocket className="h-5 w-5 mr-2" />
            Submit Infrastructure Agent
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
